import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useFlashSales } from '@/hooks/useFlashSales';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { formatCOP, formatEventDate } from '@/lib/format';
import { membershipPlanLabel } from '@/lib/membershipLabels';
import { resolveFlashSaleCta } from '@/lib/flashSaleCta';

/**
 * FlashSaleDetail — detalle de una promo flash. ROUND17 #9.
 *
 * El CTA depende de si el fan **ya tiene turno**. La Fila Virtual (C3) le asigna
 * el turno en `/flash-sales/:id/fila` y lo devuelve acá con `?turno=1`:
 *
 * - **Sin turno:** "Comprar ahora" → entra a la fila (`/flash-sales/:id/fila`).
 * - **Con turno (`?turno=1`):** "Ir a pagar" → va directo al checkout
 *   (`/checkout?mode=flash`), **sin volver a la fila.** Ese retorno a `/fila`
 *   era el bucle (turno → detalle → fila → turno → …); la decisión de ruteo
 *   vive aislada en `@/lib/flashSaleCta`.
 *
 * El permiso real de compra lo exige el checkout contra el ticket admitido en
 * `api-core`; el `?turno=1` es sólo la señal de ruteo del front.
 */
export function FlashSaleDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: sales, isLoading, isError } = useFlashSales();

  if (isLoading) {
    return (
      <section className="container-fc py-12" aria-busy="true">
        <div className="h-8 w-64 rounded bg-neutral-100" />
        <div className="mt-4 h-5 w-full max-w-md rounded bg-neutral-100" />
        <div className="mt-8 h-40 w-full rounded-card bg-neutral-100" />
      </section>
    );
  }

  const sale = sales?.find((s) => s.id === id) ?? null;

  if (isError || !sale) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">
          {isError ? 'No pudimos cargar la promo' : 'Promo no encontrada'}
        </h1>
        <p className="mt-2 text-fc-muted">
          {isError
            ? 'Probá recargar la página en unos segundos.'
            : 'La promo que buscás ya no está disponible.'}
        </p>
        <Button className="mt-6" variant="secondary" onClick={() => navigate('/flash-sales')}>
          Ver todas las promos
        </Button>
      </section>
    );
  }

  const remaining = Math.max(0, sale.quantityAvailable - sale.quantitySold);

  // `?turno=1` lo pone la Sala de Espera al asignar el turno (navigate replace).
  // Es la señal de ruteo, sin efectos: leer el estado de `useFlashSaleQueue` acá
  // METERÍA al fan en la fila con sólo abrir el detalle (el hook hace join).
  const hasTurn = searchParams.get('turno') === '1';
  const cta = resolveFlashSaleCta({ saleId: sale.id, hasTurn, remaining });

  const handleBuy = (): void => {
    if (cta.to) navigate(cta.to);
  };

  return (
    <section className="container-fc py-8 md:py-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        <div className="md:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="warning">{Math.round(sale.discountPercentage)}% OFF</Badge>
            <Badge tone="info">Stock: {remaining}</Badge>
            {sale.membershipRequired && (
              <Badge tone="neutral">
                {/* ROUND19 #11: slug `first_class` → "FirstClass" legible. */}
                Requiere membresía {membershipPlanLabel(sale.membershipRequired)}
              </Badge>
            )}
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl">{sale.title}</h1>
          {sale.description && <p className="mt-3 text-base text-fc-ink">{sale.description}</p>}

          <dl className="mt-6 grid grid-cols-1 gap-y-2 text-sm md:grid-cols-2">
            <dt className="text-fc-muted">Vence</dt>
            <dd className="text-fc-ink">{formatEventDate(sale.endDate)}</dd>
            {sale.maxPerUser !== null && (
              <>
                <dt className="text-fc-muted">Máximo por usuario</dt>
                <dd className="text-fc-ink">{sale.maxPerUser}</dd>
              </>
            )}
          </dl>

          {sale.termsConditions && (
            <Card variant="ghost" padded className="mt-6">
              <CardTitle>Términos y condiciones</CardTitle>
              <p className="mt-3 text-sm text-fc-muted">{sale.termsConditions}</p>
            </Card>
          )}
        </div>

        <Card variant="outlined" padded>
          <div className="text-xs uppercase tracking-wide text-fc-muted">Precio promo</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums">
            {formatCOP(sale.finalPrice)}
          </div>
          {sale.originalPrice > sale.finalPrice && (
            <div className="text-sm tabular-nums text-fc-muted line-through">
              {formatCOP(sale.originalPrice)}
            </div>
          )}
          <Button
            className="mt-5 w-full justify-center"
            size="lg"
            onClick={handleBuy}
            disabled={cta.to === null}
          >
            {cta.label}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full justify-center"
            onClick={() => navigate('/flash-sales')}
          >
            Volver al listado
          </Button>
        </Card>
      </div>
    </section>
  );
}
